/**
 * Canonical NAP + social profiles for on-page contact and JSON-LD.
 * Keep SEO schema, footer, contact page, /card, and llms.txt in sync via this module.
 *
 * sameAs: only profiles that exist. Add GOOGLE_BUSINESS_PROFILE_URL when you have
 * the Maps share link (g.page / maps.app.goo.gl / maps?cid=…). Do not invent
 * Wikidata, Clutch, Crunchbase, or aggregateRating.
 */

export const BRAND_NAME = "Jamil Global";
export const BRAND_ALTERNATE_NAMES = [
  "Jamil Global AI",
  "Jamil Global Kuwait",
  "Jamil Global AI Agency",
];

export const CONTACT_EMAIL = "hello@jamilglobal.com";

/** Primary market phone (Kuwait) — prefer in schema `telephone`. */
export const PHONE_KUWAIT = {
  tel: "+96565661787",
  display: "+965 65661787",
  e164Digits: "96565661787",
};

/** UAE / WhatsApp line — keep as secondary ContactPoint. */
export const PHONE_UAE = {
  tel: "+971585099750",
  display: "+971 58 509 9750",
  e164Digits: "971585099750",
};

export const WHATSAPP_URL = `https://wa.me/${PHONE_UAE.e164Digits}`;

/** Free 30-minute consultation booking. */
export const CALENDLY_URL = "https://calendly.com/danyal-jamil/30min";

/**
 * Registration / public NAP — United Arab Emirates (Sharjah).
 * Jojo/Danyal (2026-10-08): country + emirate only — no locality, street, or postal code.
 * Kuwait remains a primary market in areaServed (expanding to Kuwait).
 */
export const ADDRESS = {
  addressCountry: "AE",
  addressCountryName: "United Arab Emirates",
  addressRegion: "Sharjah",
  /** Human-readable one-liner for on-page copy */
  display: "Registered in the UAE, expanding to Kuwait",
};

/** Citeable portfolio facts — keep About, Home, hubs, schema, and llms in sync. */
export const AD_SPEND_MANAGED = "$15M+";
export const CLIENTS_SERVED = "71 and counting";
export const LOCATION_LINE = "Registered in the UAE, expanding to Kuwait";

/**
 * Geo intentionally omitted (Jojo/Danyal 2026-10-08): no lat/long on public entity.
 */
export const GEO_COORDINATES = null;

/** ISO date: company filing / founding (October 2023). */
export const FOUNDING_DATE = "2023-10";

/**
 * Honest headcount signal: one core builder; specialists onboarded per project.
 * Do not inflate — contradicts /about and FAQ copy.
 */
export const NUMBER_OF_EMPLOYEES = 1;

/** LocalBusiness / ProfessionalService price band (agency mid-market). */
export const PRICE_RANGE = "$$";

/**
 * Google Business Profile public Maps URL — paste share link here when available.
 * Example: https://maps.app.goo.gl/... or https://g.page/...
 */
export const GOOGLE_BUSINESS_PROFILE_URL =
  "https://share.google/vXPy8vgjJeKeOvD2T";

/** Organization sameAs — company profiles only (person LinkedIn lives on Person). */
export const SOCIAL_PROFILES = [
  "https://www.linkedin.com/company/jamil-global/",
].filter(Boolean);

export const sameAsProfiles = () =>
  [
    ...SOCIAL_PROFILES,
    GOOGLE_BUSINESS_PROFILE_URL || null,
  ].filter(Boolean);

/**
 * Named founder / primary human author — E-E-A-T Person entity.
 * Author page: /authors/danyal-jamil
 */
export const FOUNDER = {
  name: "Danyal Jamil",
  givenName: "Danyal",
  familyName: "Jamil",
  jobTitle: "Founder & Principal Builder",
  credentials:
    "Ships AI agents, automation, and product systems for Kuwait and GCC teams",
  email: CONTACT_EMAIL,
  urlPath: "/authors/danyal-jamil",
  linkedIn: "https://www.linkedin.com/in/d3nyal/",
  imagePath: "/logo.png",
  knowsAbout: [
    "AI Agents",
    "AI Automation",
    "AI Consultancy",
    "Workflow Automation",
    "Software Development",
    "Kuwait",
    "GCC",
  ],
};

export const personSameAs = () => [FOUNDER.linkedIn].filter(Boolean);

export const KNOWS_ABOUT = [
  "AI Agents",
  "AI Automation",
  "AI Integration",
  "AI Consultancy in Kuwait",
  "Document AI",
  "Workflow Automation",
  "Artificial Intelligence",
  "AI Consultancy",
  "Software Development",
  "SaaS Development",
  "Kuwait",
  "GCC",
];

/** PostalAddress node for schema.org */
export const postalAddressSchema = () => {
  const node = {
    "@type": "PostalAddress",
    addressCountry: ADDRESS.addressCountry,
  };
  if (ADDRESS.streetAddress) node.streetAddress = ADDRESS.streetAddress;
  if (ADDRESS.addressLocality) node.addressLocality = ADDRESS.addressLocality;
  if (ADDRESS.addressRegion) node.addressRegion = ADDRESS.addressRegion;
  return node;
};

/** Person JSON-LD for founder / article authors */
export const personSchema = (baseUrl) => ({
  "@type": "Person",
  "@id": `${baseUrl}/#person-danyal-jamil`,
  name: FOUNDER.name,
  givenName: FOUNDER.givenName,
  familyName: FOUNDER.familyName,
  jobTitle: FOUNDER.jobTitle,
  description: FOUNDER.credentials,
  url: `${baseUrl}${FOUNDER.urlPath}`,
  email: FOUNDER.email,
  image: `${baseUrl}${FOUNDER.imagePath}`,
  worksFor: { "@id": `${baseUrl}/#organization` },
  knowsAbout: FOUNDER.knowsAbout,
  sameAs: personSameAs(),
  address: postalAddressSchema(),
});

/** Shared Organization + LocalBusiness + ProfessionalService entity for JSON-LD graphs */
export const organizationSchema = (baseUrl) => ({
  "@type": ["Organization", "LocalBusiness", "ProfessionalService"],
  "@id": `${baseUrl}/#organization`,
  name: BRAND_NAME,
  alternateName: BRAND_ALTERNATE_NAMES,
  url: baseUrl,
  logo: `${baseUrl}/logo.png`,
  image: `${baseUrl}/og-image.png`,
  email: CONTACT_EMAIL,
  telephone: PHONE_KUWAIT.tel,
  priceRange: PRICE_RANGE,
  description:
    "AI studio registered in the UAE and expanding to Kuwait. We build agents and automations that survive contact with real operations — reporting loops, document pipelines, and growth systems for Kuwait and GCC teams.",
  slogan: "AI that shows up in operations—not only in decks",
  foundingDate: FOUNDING_DATE,
  numberOfEmployees: {
    "@type": "QuantitativeValue",
    value: NUMBER_OF_EMPLOYEES,
    unitText: "core builder; specialists onboarded per project",
  },
  foundingLocation: {
    "@type": "Place",
    name: "Sharjah, United Arab Emirates",
    address: postalAddressSchema(),
  },
  address: postalAddressSchema(),
  ...(GEO_COORDINATES
    ? {
        geo: {
          "@type": "GeoCoordinates",
          latitude: GEO_COORDINATES.latitude,
          longitude: GEO_COORDINATES.longitude,
        },
      }
    : {}),
  areaServed: [
    { "@type": "Country", name: "Kuwait" },
    { "@type": "Country", name: "United Arab Emirates" },
    { "@type": "Country", name: "Saudi Arabia" },
    { "@type": "Country", name: "Qatar" },
  ],
  knowsAbout: KNOWS_ABOUT,
  sameAs: sameAsProfiles(),
  founder: { "@id": `${baseUrl}/#person-danyal-jamil` },
  employee: { "@id": `${baseUrl}/#person-danyal-jamil` },
  contactPoint: [
    {
      "@type": "ContactPoint",
      telephone: PHONE_KUWAIT.tel,
      contactType: "sales",
      areaServed: "KW",
      availableLanguage: ["English", "Arabic"],
      email: CONTACT_EMAIL,
    },
    {
      "@type": "ContactPoint",
      telephone: PHONE_UAE.tel,
      contactType: "customer service",
      areaServed: ["KW", "AE", "SA", "QA"],
      availableLanguage: ["English", "Arabic"],
      email: CONTACT_EMAIL,
      url: WHATSAPP_URL,
    },
  ],
});
